import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, Mail, Trash2, LogOut, RefreshCw, MapPin } from "lucide-react";
import Seo from "../components/site/Seo";
import { Logo } from "../components/site/Logo";
import { api } from "../lib/api";

const STATUSES = { nouveau: "Nouveau", contacte: "Contacté", devis: "Devis envoyé", gagne: "Gagné", perdu: "Perdu" };
const COLORS = { nouveau: "bg-lime text-forest", contacte: "bg-sand text-forest", devis: "bg-fern text-forest", gagne: "bg-moss text-cream", perdu: "bg-ink/10 text-ink/70" };

const LeadCard = ({ l, onStatus, onDelete }) => (
  <article className="rounded-2xl bg-white border border-moss/15 p-5 md:p-6" data-testid={`lead-card-${l.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-xl font-extrabold text-forest">{l.name}</h3>
        <p className="text-xs text-sage mt-1">{new Date(l.created_at).toLocaleString("fr-FR")} · depuis {l.source_page}{l.email_sent ? " · email envoyé" : ""}</p>
      </div>
      <span className={`rounded-full px-3 py-1 text-xs font-bold ${COLORS[l.status]}`}>{STATUSES[l.status]}</span>
    </div>
    <div className="mt-4 grid sm:grid-cols-3 gap-2 text-sm">
      <a href={`tel:${l.phone.replace(/\s/g, "")}`} className="flex items-center gap-2 text-moss font-semibold"><Phone className="w-4 h-4" />{l.phone}</a>
      {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-moss break-all"><Mail className="w-4 h-4 shrink-0" />{l.email}</a>}
      <span className="flex items-center gap-2 text-ink/80"><MapPin className="w-4 h-4" />{l.city}</span>
    </div>
    <p className="mt-3 text-sm text-ink/70"><strong className="text-forest">{l.place_type}</strong>{l.surface ? ` · ${l.surface}` : ""}</p>
    {l.message && <p className="mt-3 text-[15px] text-ink/85 bg-cream rounded-xl p-3 whitespace-pre-line">{l.message}</p>}
    <div className="mt-4 flex flex-wrap items-center gap-2">
      <select value={l.status} onChange={e => onStatus(l.id, e.target.value)} className="rounded-full border border-moss/20 px-3 py-2 text-sm bg-white" data-testid={`lead-status-${l.id}`}>
        {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <button onClick={() => onDelete(l.id)} className="ml-auto inline-flex items-center gap-1.5 text-sm text-red-700 hover:underline" data-testid={`lead-delete-${l.id}`}><Trash2 className="w-4 h-4" />Supprimer</button>
    </div>
  </article>
);

export default function AdminDashboard() {
  const nav = useNavigate();
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) nav("/admin/login");
      else setLeads([]);
    }
  }, [nav]);

  useEffect(() => { load(); }, [load]);

  const onStatus = async (id, status) => {
    await api.patch(`/leads/${id}`, { status });
    setLeads(ls => ls.map(l => (l.id === id ? { ...l, status } : l)));
  };
  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    await api.delete(`/leads/${id}`);
    setLeads(ls => ls.filter(l => l.id !== id));
  };
  const logout = async () => { await api.post("/auth/logout"); nav("/admin/login"); };

  const shown = useMemo(() => (leads || []).filter(l => filter === "all" || l.status === filter), [leads, filter]);
  const counts = useMemo(() => Object.fromEntries(Object.keys(STATUSES).map(k => [k, (leads || []).filter(l => l.status === k).length])), [leads]);

  return (
    <main className="min-h-screen bg-cream" data-testid="page-admin">
      <Seo title="Demandes de devis | Espace pro" description="Espace professionnel" path="/admin" noindex />
      <header className="bg-forest text-cream">
        <div className="max-w-6xl mx-auto px-5 h-20 flex items-center justify-between">
          <Logo light />
          <div className="flex gap-2">
            <button onClick={load} className="btn btn-ghost-light !py-2" data-testid="admin-refresh"><RefreshCw className="w-4 h-4" /></button>
            <button onClick={logout} className="btn btn-ghost-light !py-2" data-testid="admin-logout"><LogOut className="w-4 h-4" />Déconnexion</button>
          </div>
        </div>
      </header>
      <section className="max-w-6xl mx-auto px-5 py-10">
        <h1 className="font-display text-4xl font-extrabold text-forest">Demandes de devis</h1>
        <div className="mt-6 flex flex-wrap gap-2" data-testid="admin-filters">
          <button onClick={() => setFilter("all")} className={`rounded-full px-4 py-2 text-sm font-bold ${filter === "all" ? "bg-forest text-cream" : "bg-white border border-moss/15"}`}>Toutes ({leads?.length ?? 0})</button>
          {Object.entries(STATUSES).map(([k, v]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`} className={`rounded-full px-4 py-2 text-sm font-bold ${filter === k ? "bg-forest text-cream" : "bg-white border border-moss/15"}`}>{v} ({counts[k] ?? 0})</button>
          ))}
        </div>
        <div className="mt-8 grid gap-4" data-testid="leads-list">
          {leads === null && <p className="text-sage">Chargement…</p>}
          {leads !== null && shown.length === 0 && <p className="text-sage" data-testid="leads-empty">Aucune demande pour le moment.</p>}
          {shown.map(l => <LeadCard key={l.id} l={l} onStatus={onStatus} onDelete={onDelete} />)}
        </div>
      </section>
    </main>
  );
}
